import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './AdminLoginPage.module.css';

export function AdminLoginPage() {
  usePageTitle('Admin — Login');
  const { login, error, clearError, isAdminAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Return to the page the admin was headed for (set by AdminRoute), falling
  // back to the orders list. Guard against looping back to login itself.
  const from = (location.state as { from?: string } | null)?.from;
  const redirectTo = from && !from.startsWith('/admin/login') ? from : '/admin/orders';

  useEffect(() => {
    if (isAdminAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAdminAuthenticated, navigate, redirectTo]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    await login(password, totpCode.trim() || undefined);
    setSubmitting(false);
  }

  return (
    <main className={styles.page}>
      <h1 className={styles.heading}>Admin Login</h1>
      <form onSubmit={handleSubmit} className={styles.form}>
        <label htmlFor="admin-password" className={styles.label}>
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={submitting}
          className={styles.input}
        />
        <label htmlFor="admin-totp" className={styles.label}>
          Authenticator code
        </label>
        <input
          id="admin-totp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="6-digit code"
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
          disabled={submitting}
          className={styles.input}
        />
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        <button type="submit" disabled={submitting} className={styles.submitBtn}>
          {submitting ? 'Logging in...' : 'Log in'}
        </button>
      </form>
    </main>
  );
}
